import { reactive } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

export interface VisitedTab {
  fullPath: string
  path: string
  name: string
  title: string
  pinned: boolean
  closeConfirm: boolean
}

interface VisitedTabsState {
  tabs: VisitedTab[]
  dirtyRouteKeys: Set<string>
}

const PINNED_PATHS = new Set(['/dashboard'])
const MAX_TABS = 12

const CLOSE_CONFIRM_ROUTE_NAMES = new Set([
  'ParentProductDetail',
  'MaterialMasterDetail',
  'DesignWorkOrderDetail',
  'DesignBOMDetail',
  'TechPackView',
  'SalesOrderDetail',
  'ManufacturingOrderDetail',
  'PrototypeWorkspace',
  'TaskBoardDetail',
  'TaskDetail',
  'AdvancedBOMWorkspace',
  'OrderProcurementCenter',
  'ProductionTaskDispatcher',
])

const CLOSE_CONFIRM_TITLE_KEYWORDS = ['详情', '工作台', '评审中心']

export const visitedTabsState = reactive<VisitedTabsState>({
  tabs: [],
  dirtyRouteKeys: new Set<string>(),
})

const getRouteTitle = (route: RouteLocationNormalizedLoaded) => {
  return String(route.meta.title || route.name || route.path || '未命名页面')
}

const shouldRecordRoute = (route: RouteLocationNormalizedLoaded) => {
  if (route.meta.public || route.meta.mobile) return false
  if (route.path === '/' || route.path === '/login' || route.path === '/403') return false
  return route.matched.some(record => record.meta.requiresAuth)
}

const shouldConfirmClose = (route: RouteLocationNormalizedLoaded) => {
  if (route.meta.closeConfirm === true) return true
  if (route.name && CLOSE_CONFIRM_ROUTE_NAMES.has(String(route.name))) return true
  const title = getRouteTitle(route)
  return CLOSE_CONFIRM_TITLE_KEYWORDS.some(keyword => title.includes(keyword))
}

const trimTabs = (activeFullPath: string) => {
  while (visitedTabsState.tabs.length > MAX_TABS) {
    const removableIndex = visitedTabsState.tabs.findIndex(tab => !tab.pinned && tab.fullPath !== activeFullPath)
    if (removableIndex === -1) return
    visitedTabsState.dirtyRouteKeys.delete(visitedTabsState.tabs[removableIndex].fullPath)
    visitedTabsState.tabs.splice(removableIndex, 1)
  }
}

export function addVisitedTab(route: RouteLocationNormalizedLoaded) {
  if (!shouldRecordRoute(route)) return

  const nextTab: VisitedTab = {
    fullPath: route.fullPath,
    path: route.path,
    name: route.name ? String(route.name) : route.path,
    title: getRouteTitle(route),
    pinned: PINNED_PATHS.has(route.path),
    closeConfirm: shouldConfirmClose(route),
  }

  const existingIndex = visitedTabsState.tabs.findIndex(tab => tab.fullPath === nextTab.fullPath)
  if (existingIndex >= 0) {
    visitedTabsState.tabs[existingIndex] = nextTab
    return
  }

  if (nextTab.pinned) {
    visitedTabsState.tabs.unshift(nextTab)
  } else {
    visitedTabsState.tabs.push(nextTab)
  }

  trimTabs(route.fullPath)
}

export function removeVisitedTab(fullPath: string) {
  const index = visitedTabsState.tabs.findIndex(tab => tab.fullPath === fullPath)
  if (index === -1 || visitedTabsState.tabs[index].pinned) return
  visitedTabsState.dirtyRouteKeys.delete(fullPath)
  visitedTabsState.tabs.splice(index, 1)
}

export function removeOtherVisitedTabs(activeFullPath: string) {
  visitedTabsState.tabs = visitedTabsState.tabs.filter(tab => tab.pinned || tab.fullPath === activeFullPath)
  for (const key of Array.from(visitedTabsState.dirtyRouteKeys)) {
    if (!visitedTabsState.tabs.some(tab => tab.fullPath === key)) {
      visitedTabsState.dirtyRouteKeys.delete(key)
    }
  }
}

export function removeAllClosableVisitedTabs() {
  visitedTabsState.tabs = visitedTabsState.tabs.filter(tab => tab.pinned)
  visitedTabsState.dirtyRouteKeys.clear()
}

export function clearVisitedTabs() {
  visitedTabsState.tabs = []
  visitedTabsState.dirtyRouteKeys.clear()
}

export function markVisitedTabDirty(fullPath: string, dirty = true) {
  if (!fullPath) return
  if (dirty) {
    visitedTabsState.dirtyRouteKeys.add(fullPath)
  } else {
    visitedTabsState.dirtyRouteKeys.delete(fullPath)
  }
}

export function isVisitedTabDirty(fullPath: string) {
  return visitedTabsState.dirtyRouteKeys.has(fullPath)
}
